import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, AlertTriangle } from 'lucide-react';
import api from '../utils/axios';

// Force inline styles to have priority over any injected styles
const forceStylePriority = () => {
  document.querySelectorAll('*[style]').forEach(el => {
    const inlineBg = el.style.backgroundColor;
    const inlineColor = el.style.color;
    if (inlineBg && inlineBg !== 'transparent') {
      el.style.setProperty('background-color', inlineBg, 'important');
    }
    if (inlineColor) {
      el.style.setProperty('color', inlineColor, 'important');
    }
  });
};

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTestCredentials, setShowTestCredentials] = useState(true);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    // Run style enforcement on mount and periodically
    forceStylePriority();
    const interval = setInterval(forceStylePriority, 200);
    setTimeout(() => clearInterval(interval), 5000);
    
    // Load system config to check if we should show test credentials
    const loadConfig = async () => {
      try {
        const [configRes, brandingRes] = await Promise.all([
          api.get('/config/system').catch(() => ({ data: {} })),
          api.get('/branding/public').catch(() => ({ data: null })),
        ]);
        setShowTestCredentials(configRes.data.mostrar_credenciales_prueba !== false);
        if (brandingRes.data) setBranding(brandingRes.data);
      } catch (error) {
        setShowTestCredentials(true);
      }
    };
    loadConfig();
    
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // Verificar si debe cambiar contraseña
      if (result.must_change_password) {
        toast.info('Debes cambiar tu contraseña temporal');
        navigate('/force-change-password');
      } else {
        toast.success('Sesión iniciada correctamente');
        navigate('/');
      }
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image/Brand */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1554290671-aa496cf17b4a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyYWwlMjBnbGFzcyUyMGJ1aWxkaW5nJTIwZGV0YWlsJTIwYmx1ZSUyMGdyZXl8ZW58MHx8fHwxNzY4ODA0NTYwfDA&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(15, 23, 42, 0.7)' }}></div>
        <div className="relative z-10 flex flex-col justify-center items-center p-12" style={{ color: '#ffffff' }}>
          {branding?.logo_login ? (
            <img src={branding.logo_login} alt={branding.nombre_empresa || 'Logo'} className="mb-6 max-h-16 object-contain" />
          ) : (
            <Shield size={64} className="mb-6" style={{ color: '#ffffff' }} />
          )}
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-4" style={{ color: '#ffffff' }}>
            {branding?.nombre_empresa || 'AlertGuard'}
          </h1>
          <p className="text-xl text-center" style={{ color: '#cbd5e1' }}>
            Sistema de Monitoreo Transaccional
          </p>
          <p className="text-sm mt-4 text-center max-w-md" style={{ color: '#94a3b8' }}>
            Protección avanzada contra fraude y lavado de activos mediante
            detección inteligente de patrones sospechosos
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ backgroundColor: '#ffffff' }}>
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-heading font-bold tracking-tight" style={{ color: '#0f172a' }}>
              Iniciar Sesión
            </h2>
            <p className="mt-2" style={{ color: '#475569' }}>
              Accede a la plataforma de monitoreo
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium" style={{ color: '#334155' }}>
                Correo Electrónico
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="usuario@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-slate-200"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium" style={{ color: '#334155' }}>
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-slate-200"
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full font-semibold py-3 rounded-md transition-colors"
              style={{ 
                backgroundColor: '#2563eb !important', 
                color: '#ffffff !important',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '44px'
              }}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>

          {showTestCredentials && (
            <div className="mt-6 p-4 border rounded-md" style={{ backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }}>
              <div className="flex items-start space-x-2">
                <AlertTriangle size={20} className="mt-0.5" style={{ color: '#2563eb' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#1e3a8a' }}>Credenciales de prueba</p>
                  <p className="text-xs mt-1" style={{ color: '#1d4ed8' }}>
                    Email: <span className="font-mono">admin@alertguard.com</span>
                  </p>
                  <p className="text-xs" style={{ color: '#1d4ed8' }}>
                    Password: <span className="font-mono">admin123</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
